import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import { Button } from '@/modules/ui/components/button/Button';

interface JobDescriptionProps {
  /** The Public Job Description: plain text from a textarea, so line breaks are its only formatting. */
  text: string;
}

/**
 * On phones the description sits above the Apply form, so it starts clamped
 * with a toggle; wider screens show it whole beside the form.
 */
export function JobDescription({ text }: JobDescriptionProps) {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);

  // Measured, not guessed from length: the clamp only applies below `md`, and
  // the viewport can cross it after load.
  useEffect(() => {
    const element = textRef.current;
    if (!element) return;
    const observer = new ResizeObserver(() =>
      setIsClamped(element.scrollHeight > element.clientHeight + 1),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="flex flex-col gap-3 border-t pt-8">
      <h2 className="text-muted-foreground text-[13px] font-semibold">
        About the role
      </h2>
      <p
        className={cn(
          'max-w-[68ch] text-base leading-[1.7] whitespace-pre-line',
          // A fade, not line-clamp: its ellipsis lands on a blank line when the cut falls between paragraphs.
          !isExpanded &&
            'max-md:max-h-40 max-md:overflow-hidden max-md:[mask-image:linear-gradient(to_bottom,black_55%,transparent)]',
        )}
        ref={textRef}
      >
        {text}
      </p>
      {(isClamped || isExpanded) && (
        <Button
          aria-expanded={isExpanded}
          className="gap-1 self-start px-0 font-semibold has-[>svg]:px-0 md:hidden"
          onClick={() => setIsExpanded((expanded) => !expanded)}
          variant="link"
        >
          {isExpanded ? 'Show less' : 'Read full description'}
          <ChevronDown
            aria-hidden
            className={cn(
              'size-4 transition-transform',
              isExpanded && 'rotate-180',
            )}
          />
        </Button>
      )}
    </section>
  );
}
